import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export interface RelevanceFact {
  id: string;
  paragraph_number: number | null;
  allegation_text: string | null;
  verdict: string;                 // required_for | supports
  review_status: string | null;    // agreed | split
  proposer_verdict: string | null;
  judge_verdict: string | null;
  rationale: string | null;
  human_status: string;            // pending | verified | rejected | adjusted
  human_verdict: string | null;    // human-corrected materiality when adjusted
  human_note: string | null;
  needs_review: boolean;
}
export interface RelevanceElement {
  element_id: string;
  element_name: string;
  must_prove: string | null;
  facts: RelevanceFact[];
}
export interface RelevanceClaim {
  claim_id: string;
  claim_name: string;
  target: string | null;
  total_elements: number;
  supported_elements: number;
  elements: RelevanceElement[];
}

export type RelevanceVerdict = "required_for" | "supports";
export type RelevanceHumanStatus = "verified" | "rejected" | "adjusted" | "pending";

/** Layer 2: per claim -> element -> the facts material to it. */
export function useCaseRelevance(caseId: string | undefined) {
  return useQuery<RelevanceClaim[], Error>({
    queryKey: ["case-relevance", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr().rpc("fetch_case_relevance", { p_case_id: caseId });
      if (error) throw new Error(error.message);
      return (data as RelevanceClaim[]) ?? [];
    },
  });
}

/** Record a human decision on one relevance link (adjust = required_for <-> supports). */
export function useSetRelevanceReview(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: RelevanceHumanStatus; note?: string; verdict?: string | null }) => {
      const { error } = await berserkr().rpc("set_relevance_review", {
        p_id: args.id, p_status: args.status, p_note: args.note ?? null, p_verdict: args.verdict ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case-relevance", caseId] }),
  });
}

/** Bulk verify all pending material links (optionally scoped to one claim). */
export function useBulkVerifyRelevance(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (claimId?: string) => {
      const { data, error } = await berserkr().rpc("set_all_relevance_review", {
        p_case_id: caseId, p_status: "verified", p_claim_id: claimId ?? null,
      });
      if (error) throw new Error(error.message);
      return (data as number) ?? 0;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case-relevance", caseId] }),
  });
}
